// Jugador controlado por IA. Tres niveles con estrategia modular.
// La búsqueda de palabras es inyectable: por defecto síncrona (pruebas), o vía Web Worker (App).
import { findBestWord, findEvolution } from './aiSearch.js';
import { AI_LEVEL } from '../utils/constants.js';
// La IA busca sobre un vocabulario de palabras COMUNES (semilla) para ser rápida y
// jugar palabras naturales. La validación de las jugadas usa el diccionario RAE completo.
import seedDict from '../data/dictionary-seed-es.json';

const LEVEL_OPTS = {
  // Fácil: sigue FLOJO (vocabulario semilla pequeño, sin optimizar el vaciado) para que
  // los jugadores nuevos puedan ganar.
  [AI_LEVEL.EASY]: { maxLen: 4, preferLong: false, tryEvolve: false, emptyBias: false, fullDict: false },
  // Normal: PUNTO MEDIO. Diccionario completo (juega con soltura y no se atasca), pero SIN
  // la optimización agresiva de vaciado, con palabras más cortas y algo de azar (no siempre
  // la jugada perfecta) -> competente pero se le puede ganar.
  [AI_LEVEL.NORMAL]: { maxLen: 6, preferLong: false, tryEvolve: true, emptyBias: false, fullDict: true, randomize: true },
  // Difícil: fuerte. Diccionario completo + estrategia de VACIAR el atril + palabras largas.
  [AI_LEVEL.HARD]: { maxLen: 16, preferLong: true, tryEvolve: true, emptyBias: true, fullDict: true },
};

// Vocabulario semilla (solo para Fácil).
const AI_WORDS = seedDict.words;

export class AIPlayer {
  /**
   * @param {Game} game
   * @param {string} level
   * @param {object} [deps]
   * @param {(rack, dict, opts) => Promise|object} [deps.search] función de búsqueda (worker o síncrona)
   */
  constructor(game, level = AI_LEVEL.NORMAL, { search = null } = {}) {
    this.game = game;
    this.level = level;
    this.opts = LEVEL_OPTS[level] || LEVEL_OPTS[AI_LEVEL.NORMAL];
    // Normal/Difícil buscan sobre el diccionario COMPLETO (validador); Fácil, sobre la semilla.
    this.dictionary = (this.opts.fullDict && game.validator?.aiWordList)
      ? game.validator.aiWordList()
      : AI_WORDS;
    // Búsqueda por defecto: síncrona en el mismo hilo.
    this._search = search || ((rack, dict, opts) => findBestWord({ rack, dictionary: dict, opts }));
  }

  /**
   * Decide y ejecuta la jugada del turno actual (asíncrono para permitir Web Worker).
   * @returns {Promise<{ action:string, detail?:object }>}
   */
  async playTurn() {
    const player = this.game.activePlayer;
    const rack = player.rack.tiles;

    // 1) Construir la mejor palabra nueva desde la mano. Posible Web Worker.
    //    Nivel Normal (`randomize`): la longitud máxima VARÍA por turno (a veces juega
    //    palabras cortas y suelta pocas fichas) para no vaciar el atril tan rápido -> se le
    //    puede ganar. Difícil no varía: siempre busca la jugada que más vacía.
    // Longitud MÍNIMA de palabra (misma regla que el jugador): la IA tampoco juega palabras
    // más cortas (p. ej. pronombres de 2 letras).
    const minLen = this.game.config?.rules?.minWordLength ?? 2;
    let searchOpts = { ...this.opts, minLen };
    if (this.opts.randomize) {
      const lo = Math.max(3, minLen);
      const cap = Math.max(lo, this.opts.maxLen || 6);
      const maxLen = lo + Math.floor(Math.random() * (cap - lo + 1)); // longitud máx. de este turno
      searchOpts = { ...this.opts, minLen, maxLen };
    }
    const best = await this._search(rack, this.dictionary, searchOpts);
    if (best && best.tiles) {
      let wordId = null;
      for (const tile of best.tiles) {
        const real = rack.find((t) => t.instanceId === tile.instanceId);
        if (!real) continue;
        if (real.wildKind === 'universal' && tile.assignedText) real.assignedText = tile.assignedText;
        if (wordId === null) {
          this.game.placeTileFromRack(real.instanceId, null, -1);
          wordId = this.game.board.words[this.game.board.words.length - 1].id;
        } else {
          this.game.placeTileFromRack(real.instanceId, wordId, -1);
        }
      }
      const res = this.game.confirmTurn();
      if (res.ok) return { action: 'word', detail: { word: best.word, breakdown: res.breakdown } };
      this.game.resetTurn();
    }

    // 2) Si no hay palabra nueva, evolucionar una palabra del tablero (suelta 1 ficha).
    if (this.opts.tryEvolve) {
      const evo = findEvolution({ boardWords: this.game.board.words, rack, validator: this.game.validator });
      if (evo) {
        for (const t of evo.prepend) this.game.placeTileFromRack(t.instanceId, evo.wordId, 0);
        for (const t of evo.append) this.game.placeTileFromRack(t.instanceId, evo.wordId, -1);
        const res = this.game.confirmTurn();
        if (res.ok) return { action: 'evolve', detail: evo };
        this.game.resetTurn();
      }
    }

    // 3) No hay jugada: robar (si hay saco) o pasar.
    if (this.game.state.bag.length > 0) {
      this.game.drawAndPass();
      return { action: 'draw' };
    }
    this.game.passTurn();
    return { action: 'pass' };
  }
}
